/**
 * Lead Source Grouping
 *
 * Regrupuje kubełki źródeł leadów (z dashboard-source-breakdown.ts) do chipów kafelka LEADY:
 * płatne platformy osobno (META ADS / GOOGLE ADS / TIKTOK ADS), każde źródło organiczne jako
 * osobny chip (YOUTUBE ORGANIC, INSTAGRAM ORGANIC — wymóg VP-134/Miro), a `unattributed` jako
 * chip NIEPRZYPISANE. Suma chipów == suma leadów wejściowych (rekoncyliacja zachowana). Pure.
 */

export type LeadSourceBucket = { key: string; label: string; leads: number };
export type LeadChip = { label: string; value: number };

/** Etykiety chipów płatnych platform — wspólne dla kafelka LEADY i pozostałych kafelków KPI. */
export const PAID_PLATFORM_CHIP_LABELS = {
  meta: "META ADS",
  google: "GOOGLE ADS",
  tiktok: "TIKTOK ADS",
} as const;

const TIKTOK_LEAD_SOURCES = new Set(["src_tiktok", "src_tt"]);
const UNATTRIBUTED_LABEL = "NIEPRZYPISANE";
const ORGANIC_SUFFIX = "ORGANIC";
/** Limit osobnych chipów organicznych — reszta ląduje w jednym zbiorczym, żeby kafelek nie puchł. */
const MAX_ORGANIC_CHIPS = 3;
const OTHER_ORGANIC_LABEL = `POZOSTAŁE ${ORGANIC_SUFFIX}`;

/** "YouTube" → "YOUTUBE ORGANIC"; etykiety już zawierające "organic" zostają bez dopisku. */
function organicLabel(bucketLabel: string): string {
  const upper = bucketLabel.trim().toUpperCase() || "INNE";
  return upper.includes(ORGANIC_SUFFIX) ? upper : `${upper} ${ORGANIC_SUFFIX}`;
}

export function groupLeadSourceBuckets(buckets: LeadSourceBucket[]): LeadChip[] {
  const paid = { meta: 0, google: 0, tiktok: 0 };
  const organic = new Map<string, number>();
  let unattributed = 0;

  for (const bucket of buckets || []) {
    const leads = bucket.leads || 0;
    if (bucket.key === "meta_paid") paid.meta += leads;
    else if (bucket.key === "google_paid") paid.google += leads;
    else if (TIKTOK_LEAD_SOURCES.has(bucket.key)) paid.tiktok += leads;
    else if (bucket.key === "unattributed") unattributed += leads;
    else {
      const label = organicLabel(bucket.label);
      organic.set(label, (organic.get(label) ?? 0) + leads);
    }
  }

  const chips: LeadChip[] = [];
  if (paid.meta > 0) chips.push({ label: PAID_PLATFORM_CHIP_LABELS.meta, value: paid.meta });
  if (paid.google > 0) chips.push({ label: PAID_PLATFORM_CHIP_LABELS.google, value: paid.google });
  if (paid.tiktok > 0) chips.push({ label: PAID_PLATFORM_CHIP_LABELS.tiktok, value: paid.tiktok });

  // Najsilniejsze źródła organiczne osobno, ogon zwinięty — suma nadal domyka się do nagłówka.
  const organicSorted = [...organic.entries()]
    .filter(([, leads]) => leads > 0)
    .sort((a, b) => b[1] - a[1]);
  for (const [label, leads] of organicSorted.slice(0, MAX_ORGANIC_CHIPS)) {
    chips.push({ label, value: leads });
  }
  const organicTail = organicSorted
    .slice(MAX_ORGANIC_CHIPS)
    .reduce((sum, [, leads]) => sum + leads, 0);
  if (organicTail > 0) chips.push({ label: OTHER_ORGANIC_LABEL, value: organicTail });

  if (unattributed > 0) chips.push({ label: UNATTRIBUTED_LABEL, value: unattributed });
  return chips;
}
